import type {
    MeditationOpening,
    MeditationPauseMultiplier,
    MeditationType,
} from "~/features/meditations/types";
import type { BillingBlockedPayload } from "~/features/billing/types";

// AIDEV-NOTE: Playback phases form a state machine: idle -> intro -> gap -> main -> complete
export type PlaybackPhase = "idle" | "intro" | "gap" | "main" | "complete";

export type MeditationPlaybackType =
    | "hls-js"
    | "native-hls"
    | "mediasource"
    | "url-fallback"
    | "unknown";

export type MeditationPlaybackBreakType =
    | "bootstrap_http_error"
    | "bootstrap_missing_url"
    | "bootstrap_network_error"
    | "bootstrap_duplicate"
    | "hls_manifest_error"
    | "hls_fragment_error"
    | "hls_buffer_error"
    | "hls_fatal_other"
    | "media_error"
    | "play_rejected"
    | "startup_timeout"
    | "stall"
    | "unexpected_pause"
    | "unexpected_end"
    | "sse_error";

export type BufferedRangeTuple = [number, number];

export interface MeditationPlaybackSessionContext {
    playbackSessionId: string | null;
    meditationId?: string;
    sessionId?: string;
    partInteractionId?: string;
    voice: string;
    pauseMultiplier: MeditationPauseMultiplier;
    startCalledAtMs: number | null;
}

export interface MeditationPlaybackBreakLogData {
    event: "meditation.playback.break";
    breakType: MeditationPlaybackBreakType;
    playbackSessionId: string | null;
    meditationId?: string;
    sessionId?: string;
    partInteractionId?: string;
    voice: string;
    pauseMultiplier: MeditationPauseMultiplier;
    phase: PlaybackPhase;
    playbackType: MeditationPlaybackType;
    isReconnecting: boolean;
    timeSincePlaybackStartMs: number | null;
    timeSinceStartCalledMs: number | null;
    currentTime: number | null;
    readyState: number | null;
    networkState: number | null;
    paused: boolean | null;
    bufferedRanges: BufferedRangeTuple[];
    // AIDEV-NOTE: seekableEnd / duration from the <audio> element. Used as a
    // fallback for totalDuration when the reducer-derived value is not finite
    // (observed on natural HLS endings where <audio>.duration can be NaN or
    // Infinity). Without this, playback.end events drop totalDuration and
    // tailDeltaSeconds cannot be computed server-side.
    seekableEnd?: number | null;
    duration?: number | null;
    httpStatus?: number;
    responseMessage?: string;
    errorName?: string;
    errorMessage?: string;
    mediaErrorCode?: number;
    mediaErrorMessage?: string;
    hlsErrorType?: string;
    hlsErrorDetails?: string;
    hlsResponseCode?: number;
    hlsUrl?: string;
    hlsFatal?: boolean;
    stallDurationMs?: number;
    expectedDurationMs?: number;
    actualDurationMs?: number;
    // AIDEV-NOTE: Fraction of meditation played (0-1), used for severity classification
    playedFraction?: number;
    totalDurationMs?: number | null;
    alreadyPlaying?: boolean;
    priorPlaybackSessionId?: string | null;
    // AIDEV-NOTE: Timeout classification fields for Datadog indexing
    timeoutClassification?: string;
    timeoutExtensionCount?: number;
    playbackPath?: string;
    bufferedEnd?: number | null;
    requiredBuffer?: number;
    playRequested?: boolean;
}

// Gap duration in seconds between intro and main audio
export const GAP_DURATION = 1.5;

// AIDEV-NOTE: Complete state shape for streaming meditation playback
// AIDEV-NOTE: Intro now streams like main (no more introUrl)
export interface StreamingState {
    phase: PlaybackPhase;
    isPlaying: boolean;
    isLoading: boolean;
    isGenerating: boolean;
    isReconnecting: boolean;
    error: string | null;
    billingBlocked: BillingBlockedPayload | null;

    introDuration: number;
    mainDuration: number;

    currentTime: number;

    // Internal flags to track audio readiness
    introAudioReady: boolean;
    mainAudioReady: boolean;

    // AIDEV-NOTE: Debug info - whether this meditation was served from cache or freshly generated
    source: "cached" | "generated" | null;

    // AIDEV-NOTE: Set true when the player observes 2+ stall events in the
    // first 10 seconds of playback startup. Surfaces a UI warning so the
    // user understands slow/jittery network is causing the poor experience
    // rather than the meditation itself being broken. Staging 2026-04-14:
    // Gina reported issues while on an 11 Mbps connection during a Zoom call.
    slowNetworkDetected: boolean;

    // AIDEV-NOTE: Set true when play() is rejected with NotAllowedError on iOS.
    // This happens when the user gesture context has expired (e.g. HLS buffering
    // took too long after the initial tap). The UI should show a "Tap to listen"
    // button so the user can provide a fresh gesture to unlock playback.
    needsUserGesture: boolean;

    // AIDEV-NOTE: EVO-1813 + Margie incident — true when the active audio element
    // has been stalled (waiting/stalled, no currentTime progress) past a ~2s
    // debounce. Intentional silence is real encoded audio, so it keeps playing
    // and never sets this. Drives the buffering spinner in both players.
    isStalled: boolean;
}

// AIDEV-NOTE: All possible actions for the state machine
// Each action represents an event that can trigger a state transition
export type StreamingAction =
    | { type: "START" }
    | { type: "INTRO_CHUNK_RECEIVED" }
    | { type: "INTRO_READY" }
    | { type: "INTRO_COMPLETE"; duration: number }
    | { type: "INTRO_PLAYBACK_STARTED" }
    | { type: "INTRO_ENDED" }
    | { type: "GAP_ENDED" }
    | { type: "CHUNK_RECEIVED" }
    | { type: "MAIN_READY" }
    | { type: "GENERATION_COMPLETE"; totalDuration: number }
    | { type: "MAIN_PLAYBACK_STARTED" }
    | { type: "MAIN_ENDED" }
    | { type: "PLAY" }
    | { type: "PAUSE" }
    | { type: "STOP" }
    | { type: "RECONNECTING" }
    | { type: "RECONNECT_SUCCESS" }
    | { type: "RECONNECT_FAILED"; message: string }
    | { type: "SEEK"; time: number }
    | { type: "TIME_UPDATE"; time: number }
    | {
          type: "ERROR";
          message: string;
          billingBlocked?: BillingBlockedPayload | null;
      }
    // AIDEV-NOTE: Browser-native duration discovery for iOS Safari fallback mode
    | { type: "DURATION_DISCOVERED"; duration: number }
    | { type: "SLOW_NETWORK_DETECTED" }
    // AIDEV-NOTE: EVO-1813 — debounced buffering stall on the active audio element
    | { type: "STALL_DETECTED" }
    | { type: "STALL_CLEARED" }
    // AIDEV-NOTE: iOS play() rejected with NotAllowedError — needs fresh user gesture
    | { type: "NEEDS_USER_GESTURE" }
    // AIDEV-NOTE: Debug metadata from server (cache status)
    | { type: "SET_SOURCE"; source: "cached" | "generated" };

// AIDEV-NOTE: SSE event types matching backend api.meditation.stream.ts
// AIDEV-NOTE: Intro now streams like main - intro_url replaced with intro_chunk/intro_complete
export interface IntroChunkEvent {
    type: "intro_chunk";
    data: string; // base64 encoded
    sequence: number;
}

export interface IntroCompleteEvent {
    type: "intro_complete";
    duration: number;
}

export interface AudioChunkEvent {
    type: "audio_chunk";
    data: string; // base64 encoded
    sequence: number;
}

export interface CompleteEvent {
    type: "complete";
    totalDuration: number;
    // AIDEV-NOTE: audioUrl provided for fallback playback on browsers without MediaSource (iOS Safari < 17.1)
    audioUrl?: string;
}

export interface SSEErrorEvent {
    type: "error";
    message: string;
    retryable: boolean;
}

// AIDEV-NOTE: Metadata event sent at start of stream to indicate cache status (debug aid)
export interface MetadataEvent {
    type: "metadata";
    source: "cached" | "generated";
}

export type SSEEvent =
    | IntroChunkEvent
    | IntroCompleteEvent
    | AudioChunkEvent
    | CompleteEvent
    | SSEErrorEvent
    | MetadataEvent;

// Hook parameters
export interface StreamingMeditationParams {
    type: MeditationType;
    voice: string;
    pauseMultiplier?: MeditationPauseMultiplier;
    opening?: MeditationOpening;
    sessionId?: string;
    partInteractionId?: string;
    // AIDEV-NOTE: Pass sessionToken for public session access to streaming endpoints.
    sessionToken?: string;
    // AIDEV-NOTE: Force fallback mode for testing (e.g., ?forceFallback=1 URL param)
    // This simulates iOS Safari behavior in browsers that have MediaSource
    forceFallback?: boolean;
    // AIDEV-NOTE: Trial flag for unified HLS playback. When enabled, browsers
    // that normally use the custom MSE stream instead request HLS bootstrap.
    forceHlsJs?: boolean;
    // AIDEV-NOTE: Pass meditationId to stream a pre-cached meditation by ID.
    // When provided, the endpoint streams the cached file directly without generation.
    // This enables fullscreen playback for existing meditations (sharing page use case).
    meditationId?: string;
    // AIDEV-NOTE: Override the SSE stream URL (default: /api/meditation/stream).
    // Used by resource exercises to point at /api/exercise/stream.
    streamUrl?: string;
    // AIDEV-NOTE: EVO-1954 — resource exercise generation params. When set,
    // exercises ride the same durable HLS bootstrap path as every other
    // meditation type; streamUrl remains only as the SSE fallback transport.
    experienceId?: string;
    selectedPartIds?: string[];
}

// State returned by the hook (public interface)
export interface StreamingMeditationState {
    // AIDEV-NOTE: The row this playback is bound to — the replayed id, or the id the
    // server created for this generation once known. Lets the journal capture save to
    // exactly this meditation instead of "the newest one for the session".
    activeMeditationId: string | null;
    phase: PlaybackPhase;
    isPlaying: boolean;
    isLoading: boolean;
    isGenerating: boolean;
    error: string | null;
    billingBlocked: BillingBlockedPayload | null;
    currentTime: number;
    totalDuration: number;
    introDuration: number;
    mainDuration: number;
    audiblePlaybackStartSeconds: number;
    awaitingAudiblePlaybackStart: boolean;
    statusLabel: string;
    // AIDEV-NOTE: Debug info - "cached" or "generated", null until metadata received
    source: "cached" | "generated" | null;
    // AIDEV-NOTE: Surfaced to the UI when 2+ stalls fire in the first 10s
    // of playback so we can show a slow-network warning banner.
    slowNetworkDetected: boolean;
    // AIDEV-NOTE: When true, play() was rejected by iOS autoplay policy
    // because the user gesture expired during HLS buffering. UI should
    // show a tap-to-listen button for a fresh gesture.
    needsUserGesture: boolean;
    // AIDEV-NOTE: EVO-1813 — true while audio is buffering/stalled mid-playback
    // (debounced ~2s). UI shows a buffering spinner; nothing is shown during
    // intentional silence because that audio keeps playing.
    isStalled: boolean;
    // AIDEV-NOTE: Browser reports no network connection (navigator.onLine).
    // Combined with isStalled to show a "check your connection" line.
    isOffline: boolean;
}

// Actions returned by the hook (public interface)
export interface StreamingMeditationActions {
    // AIDEV-NOTE: generate() starts SSE generation in the background without
    // audio playback. Chunks buffer into MediaSource silently. Call start()
    // later (in a user gesture) to unlock audio and begin playback.
    // No-op in fallback mode (iOS Safari without MediaSource).
    generate: () => void;
    start: () => void;
    play: () => void;
    pause: () => void;
    stop: () => void;
    seek: (time: number) => void;
    // AIDEV-NOTE: Called from a fresh user gesture (button tap) after
    // NotAllowedError to retry play() with valid autoplay permission.
    resumePlayback: () => void;
}
